import { useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { PdfPagePreview } from '@/features/pdf-page-preview'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  deletePdfPages,
  deletedPagesFilename,
  parsePagesToDelete,
  readPdfPageCount,
  validateFileSize,
  type PageRangeParseError,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('deletePdfPages').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; file: File }
  | { kind: 'ready'; file: File; totalPages: number }
  | { kind: 'loading'; file: File; totalPages: number }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      totalPages: number
      removedPages: number
      remainingPages: number
    }
  | { kind: 'error'; file: File | null; totalPages: number | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileBadgeProps {
  file: File
  totalPages: number | null
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, totalPages, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)}
          {totalPages !== null && ` · ${format(t.deletePdfPages.pageCount, { pages: totalPages })}`}
        </p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface PagesInputProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function PagesInput({ value, onChange, disabled }: PagesInputProps) {
  const t = useT()

  return (
    <fieldset disabled={disabled} className="space-y-2">
      <label htmlFor="delete-pdf-pages-ranges" className="block text-sm font-medium text-foreground">
        {t.deletePdfPages.pagesLabel}
      </label>
      <input
        id="delete-pdf-pages-ranges"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t.deletePdfPages.rangesPlaceholder}
        disabled={disabled}
        className={cn(
          'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'placeholder:text-muted-foreground/70',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          disabled && 'pointer-events-none opacity-50',
        )}
      />
      <p className="text-xs text-muted-foreground">
        {t.deletePdfPages.rangesHintPrefix}
        <code>2,5-7</code>
        {t.deletePdfPages.rangesHintSuffix}
      </p>
    </fieldset>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  totalPages: number
  removedPages: number
  remainingPages: number
  onReset: () => void
}

function ResultPanel({
  file,
  blob,
  objectUrl,
  totalPages,
  removedPages,
  remainingPages,
  onReset,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = deletedPagesFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.deletePdfPages.totalPages}</span>
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.deletePdfPages.removedPages}</span>
            <span className="font-medium text-foreground">{removedPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.deletePdfPages.remainingPages}</span>
            <span className="font-medium text-foreground">{remainingPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.deletePdfPages.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function UdalitStranicyIzPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [rangesInput, setRangesInput] = useState('')

  const isBusy = phase.kind === 'reading' || phase.kind === 'loading'
  const currentFile =
    phase.kind === 'reading' ||
    phase.kind === 'ready' ||
    phase.kind === 'loading' ||
    phase.kind === 'error'
      ? phase.file
      : null
  const currentTotalPages =
    phase.kind === 'ready' || phase.kind === 'loading'
      ? phase.totalPages
      : phase.kind === 'error'
        ? phase.totalPages
        : null

  const parsedRanges = useMemo(() => {
    if (currentTotalPages === null) return null
    return parsePagesToDelete(rangesInput, currentTotalPages)
  }, [currentTotalPages, rangesInput])
  const selectedPreviewPages = useMemo(() => {
    if (!parsedRanges?.ok) return undefined
    return new Set(parsedRanges.indices.map((index) => index + 1))
  }, [parsedRanges])

  function rangeErrorMessage(error: PageRangeParseError): string {
    if (error.code === 'empty_result') return t.deletePdfPages.emptyResult
    if (error.code === 'out_of_range') {
      return format(t.deletePdfPages.outOfRange, { pages: currentTotalPages ?? 0 })
    }
    if (error.code === 'reversed_range') return t.deletePdfPages.reversedRange
    return t.deletePdfPages.invalidRanges
  }

  async function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        totalPages: null,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setRangesInput('')
    setPhase({ kind: 'reading', file })

    try {
      const totalPages = await readPdfPageCount(file)
      setPhase({ kind: 'ready', file, totalPages })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.deletePdfPages.encrypted : t.deletePdfPages.parseError
      toolError(UTILITY, code)
      setPhase({ kind: 'error', file, totalPages: null, errorCode: code, message })
    }
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setRangesInput('')
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file && phase.totalPages !== null) {
      setPhase({ kind: 'ready', file: phase.file, totalPages: phase.totalPages })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const parsed = parsePagesToDelete(rangesInput, phase.totalPages)

    if (!parsed.ok) {
      toolError(UTILITY, parsed.error.code, { total_pages: phase.totalPages })
      setPhase({
        kind: 'error',
        file: phase.file,
        totalPages: phase.totalPages,
        errorCode: parsed.error.code,
        message: rangeErrorMessage(parsed.error),
      })
      return
    }

    const file = phase.file
    setPhase({ kind: 'loading', file, totalPages: phase.totalPages })
    toolInvoke(UTILITY, {
      total_pages: phase.totalPages,
      removed_pages: parsed.indices.length,
      remaining_pages: parsed.remainingPages,
    })

    const startMs = Date.now()

    try {
      const { blob, totalPages, removedPages, remainingPages } = await deletePdfPages(file, parsed.indices)
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        total_pages: totalPages,
        removed_pages: removedPages,
        remaining_pages: remainingPages,
        output_bytes: blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        totalPages,
        removedPages,
        remainingPages,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.deletePdfPages.encrypted : t.deletePdfPages.parseError
      toolError(UTILITY, code, { total_pages: phase.totalPages })
      setPhase({ kind: 'error', file, totalPages: phase.totalPages, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          totalPages={phase.totalPages}
          removedPages={phase.removedPages}
          remainingPages={phase.remainingPages}
          onReset={handleClear}
        />
      </div>
    )
  }

  const canSubmit = phase.kind === 'ready' && parsedRanges?.ok
  const visibleRangeError = parsedRanges && !parsedRanges.ok && rangesInput.trim()
    ? rangeErrorMessage(parsedRanges.error)
    : null

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge
          file={currentFile}
          totalPages={currentTotalPages}
          onClear={handleClear}
          disabled={isBusy}
        />
      ) : (
        <DropZone onFile={handleFile} disabled={isBusy} />
      )}

      {phase.kind === 'reading' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t.deletePdfPages.reading}
        </p>
      )}

      {currentTotalPages !== null && (
        <>
          <PagesInput value={rangesInput} onChange={setRangesInput} disabled={isBusy} />

          <PdfPagePreview
            file={currentFile}
            totalPages={currentTotalPages}
            selectedPages={selectedPreviewPages}
            markerTone="destructive"
            markerLabel={t.pdfPreview.removed}
          />

          {parsedRanges?.ok ? (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-4 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{t.deletePdfPages.removedPages}</span>
                  <span className="font-medium text-foreground">{parsedRanges.indices.length}</span>
                </div>
                <div className="mt-1 flex justify-between gap-2">
                  <span className="text-muted-foreground">{t.deletePdfPages.remainingPages}</span>
                  <span className="font-medium text-foreground">{parsedRanges.remainingPages}</span>
                </div>
              </CardContent>
            </Card>
          ) : visibleRangeError ? (
            <p className="text-sm text-destructive">{visibleRangeError}</p>
          ) : null}
        </>
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="sticky bottom-3 z-10 w-full shadow-lg"
        disabled={!canSubmit || isBusy}
        onClick={handleSubmit}
      >
        {phase.kind === 'loading' ? (
          <>
            <Spinner />
            {t.deletePdfPages.loading}
          </>
        ) : (
          t.deletePdfPages.submit
        )}
      </Button>
    </div>
  )
}
